import TranslationKeys from "../../../contexts/I18nContext/translationKeys";
import {
  WhmBox,
  WhmInputAdornment,
  WhmTextField,
  whmTheme,
} from "@securitize/reactjs-whm";
import { Translate, TranslateHtml } from "../../Translation/Translate";
import { InfoCard } from "../shared/InfoCard";
import React, { useContext } from "react";
import { UseQueryResult } from "@tanstack/react-query";
import { InvestorDto } from "../../../data/dtos/investor.dto";
import {
  columnBox,
  placeholderTextFieldStyle,
  textFieldStyle,
  wrapBox,
} from "../shared/styles";
import { ReactComponent as CalendarTodayOutlined } from "@securitize/assets-sec-ui/dist/icons/svg/calendar.svg";
import { I18nContext } from "../../../contexts/I18nContext";

interface InfoCardProps {
  investorInfoQuery: UseQueryResult<InvestorDto, Error>;
}

export const PersonalDetailsCard: React.FC<
  React.PropsWithChildren<InfoCardProps>
> = ({ investorInfoQuery }) => {
  const { language } = useContext(I18nContext);

  const dateConfig: { locale: string; options: Intl.DateTimeFormatOptions } = {
    locale: language === "en" ? "en-US" : "es-ES",
    options: {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "UTC",
    },
  };

  return (
    <InfoCard
      title={
        <Translate
          translationKey={
            TranslationKeys.PROFILE_PERSONAL_INFORMATION_PERSONAL_DETAILS_CARD_TITLE
          }
        />
      }
      subtitle={
        <Translate
          translationKey={
            TranslationKeys.PROFILE_PERSONAL_INFORMATION_PERSONAL_DETAILS_CARD_SUBTITLE
          }
        />
      }
      footer={
        <TranslateHtml
          translationKey={
            TranslationKeys.PROFILE_PERSONAL_INFORMATION_PERSONAL_DETAILS_CARD_FOOTER
          }
        />
      }
    >
      <WhmBox
        component="form"
        sx={{
          ...columnBox,
          marginTop: "32px",
        }}
        noValidate
        autoComplete="off"
      >
        <WhmBox
          sx={{
            ...wrapBox,
            gap: "16px",
          }}
        >
          <WhmTextField
            id="firstName"
            inputProps={{
              "data-test-id":
                "profile-personal-information-personal-details-first-name-field",
            }}
            label={
              <Translate
                translationKey={
                  TranslationKeys.PROFILE_PERSONAL_INFORMATION_FIELDS_FIRST_NAME
                }
              />
            }
            defaultValue={investorInfoQuery.data?.details.firstName}
            sx={textFieldStyle}
            disabled
          />
          <WhmTextField
            id="middleName"
            inputProps={{
              "data-test-id":
                "profile-personal-information-personal-details-middle-name-field",
            }}
            label={
              <Translate
                translationKey={
                  TranslationKeys.PROFILE_PERSONAL_INFORMATION_FIELDS_MIDDLE_NAME
                }
              />
            }
            defaultValue={investorInfoQuery.data?.details.middleName}
            sx={textFieldStyle}
            disabled
          />
          <WhmTextField
            id="lastName"
            inputProps={{
              "data-test-id":
                "profile-personal-information-personal-details-last-name-field",
            }}
            label={
              <Translate
                translationKey={
                  TranslationKeys.PROFILE_PERSONAL_INFORMATION_FIELDS_LAST_NAME
                }
              />
            }
            defaultValue={investorInfoQuery.data?.details.lastName}
            sx={textFieldStyle}
            disabled
          />
        </WhmBox>

        <div
          style={{
            display: "flex",
            gap: "16px",
            width: "100%",
            marginTop: "16px",
          }}
        >
          <WhmTextField
            id="birthdate"
            label={
              <Translate
                translationKey={
                  TranslationKeys.PROFILE_PERSONAL_INFORMATION_FIELDS_BIRTHDATE
                }
              />
            }
            type="text"
            disabled
            defaultValue={
              investorInfoQuery.data?.details.birthday !== undefined
                ? new Date(
                    investorInfoQuery.data.details.birthday,
                  ).toLocaleDateString(dateConfig.locale, dateConfig.options)
                : ""
            }
            InputLabelProps={{
              shrink: !!investorInfoQuery.data?.details.birthday,
            }}
            inputProps={{
              "data-test-id":
                "profile-personal-information-personal-details-birthdate-field",
            }}
            sx={textFieldStyle}
            InputProps={{
              endAdornment: (
                <WhmInputAdornment position="end">
                  <CalendarTodayOutlined
                    fill={whmTheme.palette.action.disabled}
                    width="24px"
                    height="24px"
                  />
                </WhmInputAdornment>
              ),
            }}
          />

          <WhmBox sx={placeholderTextFieldStyle} />
          <WhmBox sx={placeholderTextFieldStyle} />
        </div>
      </WhmBox>
    </InfoCard>
  );
};
